// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  // Callers that set their own headers (wholesale JSON calls) pass an already-encoded body.
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : body != null ? { body } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Fetch an instant tilt-deck freight quote from the local API endpoint.
//
// body: { listingId, deliveryZip, isResidential }
// See `server/api/freight-quote.js`.
export const freightQuote = body => {
  return post('/api/freight-quote', body);
};

// Look up the approximate location of a US ZIP code ({ zip, lat, lng }).
// See `server/api/zip-location.js`.
export const zipLocation = zip => {
  return post('/api/zip-location', { zip });
};

// Ask the server to email the operator about a new Verified Seller signup.
// The server reads the signed-in user from the session cookie; no body is needed.
// See `server/api/verified-seller-alert.js`.
export const verifiedSellerAlert = () => {
  return post('/api/verified-seller-alert', {});
};

// Signed-in seller asks to move to a higher plan (e.g. after reaching the listing limit).
// See `server/api/seller-upgrade-request.js`.
export const sellerUpgradeRequest = (plan, message) => {
  return post('/api/seller-upgrade-request', { plan, message });
};

// Withdraw the seller's pending plan-change request.
export const cancelSellerPlanRequest = () => {
  return post('/api/seller-upgrade-request', { action: 'cancel' });
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

// ================ Wholesale inventory (see server/api/wholesale-items.js) ================ //
// JSON in, JSON out. The server checks the signed-in user on every call.

const wholesaleJson = (method, path, body) =>
  request(path, {
    method,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

// Public: { enabled, count } for the homepage ticker. Never returns inventory or prices.
export const wholesalePublicCount = () => wholesaleJson(methods.GET, '/api/wholesale/public-count');
export const wholesaleAccess = () => wholesaleJson(methods.GET, '/api/wholesale/access');
// Public wholesale supplier application (/wholesale/apply). Emails the operator; approves nothing.
export const wholesaleSupplierApply = body =>
  wholesaleJson(methods.POST, '/api/wholesale-supplier-application', body);
// Public: homepage container quote request. Emails the operator inbox.
export const containerLeadRequest = body =>
  wholesaleJson(methods.POST, '/api/container-lead-request', body);
// Customer assistant (AI chat). See server/api/assistant.js.
export const assistantStatus = () => wholesaleJson(methods.GET, '/api/assistant/status');
export const assistantChat = body => wholesaleJson(methods.POST, '/api/assistant/chat', body);
export const assistantSendInquiry = body =>
  wholesaleJson(methods.POST, '/api/assistant/inquiry', body);
export const assistantAdminConversations = (filter = 'all') =>
  wholesaleJson(
    methods.GET,
    `/api/assistant/admin/conversations?filter=${encodeURIComponent(filter)}`
  );
export const wholesaleMyItems = () => wholesaleJson(methods.GET, '/api/wholesale/my-items');
export const wholesaleShowItem = id =>
  wholesaleJson(methods.GET, `/api/wholesale/items/${encodeURIComponent(id)}`);
export const wholesaleCreateItem = body =>
  wholesaleJson(methods.POST, '/api/wholesale/items', body);
export const wholesaleUpdateItem = (id, body) =>
  wholesaleJson(methods.PATCH, `/api/wholesale/items/${encodeURIComponent(id)}`, body);

// Dealer browse. params: { state, size, type, condition, page }
export const wholesaleBrowse = (params = {}) => {
  const qs = Object.keys(params)
    .filter(k => params[k])
    .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
    .join('&');
  return wholesaleJson(methods.GET, `/api/wholesale/items${qs ? `?${qs}` : ''}`);
};
export const wholesaleRequestPurchase = (id, body) =>
  wholesaleJson(methods.POST, `/api/wholesale/items/${encodeURIComponent(id)}/requests`, body);
export const wholesaleRequests = () => wholesaleJson(methods.GET, '/api/wholesale/requests');
export const wholesaleCancelRequest = id =>
  wholesaleJson(methods.POST, `/api/wholesale/requests/${encodeURIComponent(id)}/cancel`, {});
export const wholesaleThread = id =>
  wholesaleJson(methods.GET, `/api/wholesale/requests/${encodeURIComponent(id)}/messages`);
// photos: base64 JPEG strings from preparePhoto() in src/util/ctWholesalePhotos.js
// files: [{ name, data }] from prepareFile() in src/util/ctWholesaleFiles.js
export const wholesaleSendMessage = (id, body, photos = [], files = []) =>
  wholesaleJson(methods.POST, `/api/wholesale/requests/${encodeURIComponent(id)}/messages`, {
    body,
    ...(photos.length ? { photos } : {}),
    ...(files.length ? { files } : {}),
  });
// Admin: shows a dealer's or supplier's held file to the other side.
export const wholesaleShareAttachment = id =>
  wholesaleJson(methods.POST, `/api/wholesale/attachments/${encodeURIComponent(id)}/share`, {});
// Photos are served only to people who can open the thread (session cookie).
export const wholesaleAttachmentUrl = id => `/api/wholesale/attachments/${encodeURIComponent(id)}`;
export const wholesaleSupplierRequests = () =>
  wholesaleJson(methods.GET, '/api/wholesale/supplier-requests');
export const wholesaleDecideRequest = (id, decision, note) =>
  wholesaleJson(methods.POST, `/api/wholesale/requests/${encodeURIComponent(id)}/decision`, {
    decision,
    note,
  });

// ================ Referral partner program (see server/api/referrals.js) ================ //
// JSON in, JSON out. The server reads the signed-in user and the ct_ref cookie.

export const referralVisit = body => wholesaleJson(methods.POST, '/api/referrals/visit', body);
export const referralSignup = (body = {}) =>
  wholesaleJson(methods.POST, '/api/referrals/signup', body);
export const referralCheck = code =>
  wholesaleJson(methods.GET, `/api/referrals/check?code=${encodeURIComponent(code)}`);
export const referralMe = () => wholesaleJson(methods.GET, '/api/referrals/me');
export const referralCreate = body => wholesaleJson(methods.POST, '/api/referrals/me', body);
export const referralAdmin = () => wholesaleJson(methods.GET, '/api/referrals/admin');
export const referralAdminAdd = body =>
  wholesaleJson(methods.POST, '/api/referrals/admin/conversions', body);
export const referralAdminUpdate = (id, body) =>
  wholesaleJson(methods.PATCH, `/api/referrals/admin/conversions/${encodeURIComponent(id)}`, body);
export const referralAdminPartner = (id, body) =>
  wholesaleJson(methods.PATCH, `/api/referrals/admin/partners/${encodeURIComponent(id)}`, body);

// Container Trader load board (/loads). The server decides who may see or change anything.
const lbPath = p => `/api/loads${p}`;
const enc = encodeURIComponent;
export const loadBoardAccess = () => wholesaleJson(methods.GET, lbPath('/access'));
export const loadBoardApplyCarrier = body =>
  wholesaleJson(methods.POST, lbPath('/carriers/apply'), body);
export const loadBoardList = (params = {}) => {
  const qs = Object.keys(params)
    .filter(k => params[k])
    .map(k => `${enc(k)}=${enc(params[k])}`)
    .join('&');
  return wholesaleJson(methods.GET, lbPath(`/board${qs ? `?${qs}` : ''}`));
};
export const loadBoardMine = () => wholesaleJson(methods.GET, lbPath('/mine'));
export const loadBoardShow = id => wholesaleJson(methods.GET, lbPath(`/${enc(id)}`));
export const loadBoardCreate = body => wholesaleJson(methods.POST, lbPath('/'), body);
export const loadBoardUpdate = (id, body) =>
  wholesaleJson(methods.PUT, lbPath(`/${enc(id)}`), body);
export const loadBoardSetStatus = (id, status) =>
  wholesaleJson(methods.POST, lbPath(`/${enc(id)}/status`), { status });
export const loadBoardRequest = (id, body) =>
  wholesaleJson(methods.POST, lbPath(`/${enc(id)}/requests`), body);
export const loadBoardRequestAction = (rid, action) =>
  wholesaleJson(methods.POST, lbPath(`/requests/${enc(rid)}/${action}`), {});
export const loadBoardCarriers = () => wholesaleJson(methods.GET, lbPath('/carriers'));
export const loadBoardAdminCarriers = (status = '') =>
  wholesaleJson(methods.GET, lbPath(`/admin/carriers${status ? `?status=${enc(status)}` : ''}`));
export const loadBoardAdminSetCarrier = (userId, body) =>
  wholesaleJson(methods.POST, lbPath(`/admin/carriers/${enc(userId)}`), body);
export const loadBoardAdminLoads = () => wholesaleJson(methods.GET, lbPath('/admin/loads'));

// ================ Dealer storefronts (see server/api/storefronts.js) ================ //
// Public (or admin-only while in test). Prices are always calculated on the server.

export const storefrontsForProfile = userId =>
  wholesaleJson(methods.GET, `/api/storefronts/profile-link/${encodeURIComponent(userId)}`);
export const storefrontProfile = slug =>
  wholesaleJson(methods.GET, `/api/storefronts/${encodeURIComponent(slug)}`);
// params: { zip, residential, size }
export const storefrontOffers = (slug, params = {}) => {
  const qs = Object.keys(params)
    .filter(k => params[k] !== undefined && params[k] !== null && params[k] !== '')
    .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
    .join('&');
  return wholesaleJson(
    methods.GET,
    `/api/storefronts/${encodeURIComponent(slug)}/offers${qs ? `?${qs}` : ''}`
  );
};
export const storefrontPlaceOrder = (slug, body) =>
  wholesaleJson(methods.POST, `/api/storefronts/${encodeURIComponent(slug)}/orders`, body);
// ================ Admin: all orders with any dealer (see server/api/admin-orders.js) ================ //

export const adminAllOrders = ({ fresh } = {}) =>
  wholesaleJson(methods.GET, `/api/admin/orders${fresh ? '?fresh=1' : ''}`);
export const adminOrdersAccess = () => wholesaleJson(methods.GET, '/api/admin/orders/access');

export const storefrontAdminOrders = () =>
  wholesaleJson(methods.GET, '/api/storefronts/admin/orders');
export const storefrontAdminDecide = (id, body) =>
  wholesaleJson(
    methods.POST,
    `/api/storefronts/admin/orders/${encodeURIComponent(id)}/decision`,
    body
  );
